import { z } from 'zod'
import type { ApiClient } from './client'
import { SearchQueryError } from './search'
import { blockKind, decodeBlockSource, notificationForSave } from './workbook-blocks'
import type { WorkbookBlock } from './workbooks'

/**
 * Workbook block jobs — POST /{clusterId}/wrk/api/job/invoke, one call per block.
 *
 * Captured 2026-09-28 (docs/inventory/workbook-blocks-capture.md):
 *   dql / sql     {query, task_id?}                      CONFIRMED
 *   pythonblock   {task_id, python_block (PLAIN source)} CONFIRMED — answers {id, status} with NO data[];
 *                 the block stores base64, invoke sends it decoded (backend owner, 2026-09-28)
 *   ai            {task_id, stream, contamination_factor, result_condition, query_data}
 *                                                        CONFIRMED — answers TWO tasks; the block's is the last
 *   call / return {query: <path>/<name>, wbkid: <target id>}
 *                                                        backend owner 2026-09-28; not invoked (runs another workbook)
 *   signal        {task_id, sig_data}                    UNVERIFIED (never invoked: raises signals)
 *   notification  {task_id, query_data}                  UNVERIFIED (never invoked: sends mail)
 *
 * Chaining: every block carries the PREVIOUS block's task id — `task_id: ""`
 * when there is none (the first block, or a previous block not run yet). Text and
 * Visualization start no job — Text passes the previous task id on,
 * Visualization is never run (it charts the previous task's result).
 *
 * State, result and revoke are the search endpoints (search.ts) — same task ids.
 */

export type JobType = 'dql' | 'sql' | 'pythonblock' | 'ai' | 'call' | 'return' | 'signal' | 'notification'

export interface BlockJobContext {
  scopeId: string
  /** The previous block's task id; null = it has none yet (sent as ""). Every
   * block sends task_id, the first one included (backend owner, 2026-09-29). */
  prevTaskId: string | null
  queryTimezone: string
  /** The workbook the block runs in; null = not saved yet (legacy: 'untitled', ''). */
  workbook: { id: string; path: string; name: string } | null
  /** True for the workbook's first block (a DQL block sends `parameters` only there). */
  first: boolean
  /** Parameter values by name — `{{Name}}` in a query. A map, not the stored array
   * (an array is refused "Parameters missing"; captured 2026-09-29). */
  parameters: Record<string, string>
  /** Time travel: `query_relative_time`, the integer from `?relative=`; null = off. */
  relativeTime: number | null
}

/** `<path>/<name>` of a workbook, as `wbkname` and a signal's `Workbook` carry it. */
export function workbookRef(w: { path: string; name: string }): string {
  return `${w.path.replace(/\/+$/, '')}/${w.name}`
}

/**
 * Why a block must not be sent yet (the legacy console refuses these before the
 * request), or null. A Call / Return with no workbook chosen; a Signal with no
 * Suspect / Target mapping.
 */
export function blockRunProblem(b: WorkbookBlock): string | null {
  const kind = blockKind(b)
  if ((kind === 'call' || kind === 'return') && (!b.query || !b.path || !b.name)) {
    return 'Please select a workbook to call/return.'
  }
  if (kind === 'signal' && !Object.entries(b.raw).some(([k, v]) => /^(Suspect|Target)/.test(k) && typeof v === 'string' && v.trim())) {
    return 'Map at least one Suspect or Target field before running this signal.'
  }
  return null
}

/**
 * The invoke body for one block, or null when the kind starts no job
 * (text, visual) or is not a known kind. Per the legacy RunCurrentBlock
 * (workbook invoke payloads, 2026-09-29):
 * - common: scope_id, job_execution, query_timezone, task_id (previous block's);
 * - wbkname = `<path>/<name>` and wbkid = id of the HOST workbook — except SQL
 *   (wbkid only), AI (neither) and Call / Return (wbkid = the CALLED workbook);
 * - `parameters` ({name: value}) on a FIRST-block DQL, every Search and every SQL
 *   block; the backend fills `{{Name}}` (SQL quotes the value, DQL does not);
 * - `query_relative_time` on every block while time travel is on;
 * - not sent yet: `include_payload` (a Search checkbox) — where it is stored is pending.
 */
export function blockJobBody(b: WorkbookBlock, ctx: BlockJobContext): Record<string, unknown> | null {
  const kind = blockKind(b)
  if (kind === null || kind === 'text' || kind === 'visual') return null
  const common = {
    scope_id: ctx.scopeId,
    job_execution: 'on-demand',
    query_timezone: ctx.queryTimezone,
    task_id: ctx.prevTaskId ?? '',
    ...(ctx.relativeTime !== null ? { query_relative_time: ctx.relativeTime } : {}),
  }
  const parameters = { parameters: ctx.parameters }
  const wbkname = ctx.workbook ? workbookRef(ctx.workbook) : 'untitled'
  const wbkid = ctx.workbook?.id ?? ''
  const host = { wbkname, wbkid }
  const raw = b.raw
  switch (kind) {
    case 'dql':
      return { ...common, ...host, ...(ctx.first ? parameters : {}), job_type: 'dql', query: b.query }
    case 'gui':
      return { ...common, ...host, ...parameters, job_type: 'dql', query: b.query }
    case 'sql':
      return { ...common, wbkid, ...parameters, job_type: 'sql', query: b.query }
    case 'code':
      // Stored base64; the engine takes the source as written — never encoded on invoke.
      return { ...common, ...host, job_type: 'pythonblock', python_block: decodeBlockSource(typeof raw.code === 'string' ? raw.code : '') }
    case 'ai': {
      const d = (typeof raw.data === 'object' && raw.data !== null ? raw.data : {}) as Record<string, unknown>
      return {
        ...common,
        job_type: 'ai',
        contamination_factor: d.contamination_factor,
        result_condition: d.result_condition,
        query_data: d.query_data,
      }
    }
    case 'call':
    case 'return':
      // Calls another detection workbook: `query` is its folder path + "/" +
      // name, `wbkid` its id (the block stores the id in its own `query`).
      return { ...common, wbkname, job_type: kind, query: workbookRef(b), wbkid: b.query }
    case 'signal':
      return { ...common, ...host, job_type: 'signal', sig_data: { ...raw, Workbook: wbkname } }
    case 'notification': {
      const n = notificationForSave(b).raw
      const top = typeof n.topRows === 'string' && /^\d+$/.test(n.topRows) ? Number(n.topRows) : n.topRows
      const body = typeof n.email_body === 'string' ? n.email_body.replace(/\r?\n/g, '<br />') : n.email_body
      return { ...common, ...host, job_type: 'notification', query_data: { ...n, email_body: body, topRows: top } }
    }
  }
}

/* ------------------------------------------------------------------ schemas */

const task = z.object({ id: z.string(), status: z.string().catch('') }).passthrough()

/** Both shapes seen: `{status, data: [task…]}` (dql, sql, ai) and a bare task (pythonblock). */
export const blockInvokeResponse = z
  .object({
    status: z.string().catch(''),
    message: z.string().nullable().optional().catch(null),
    id: z.string().optional().catch(undefined),
    /** Tasks — or a refusal INSIDE a success envelope: `data: {status: 'error', message}`
     * (e.g. "Parameters missing", captured 2026-09-29). */
    data: z
      .union([z.array(task), z.object({ status: z.string().catch(''), message: z.string().catch('') }).passthrough()])
      .catch([]),
    parser_error_info: z
      .object({ error_msg: z.string().nullable().catch(null), parsed_query: z.string().nullable().catch(null) })
      .passthrough()
      .nullable()
      .optional()
      .catch(null),
  })
  .passthrough()

/** The task id a block's result lives under: the LAST task (ai starts two), or the bare id. */
export function blockTaskId(res: z.infer<typeof blockInvokeResponse>): string | null {
  return (Array.isArray(res.data) ? res.data.at(-1)?.id : undefined) ?? res.id ?? null
}

export const outlierRecommendationsResponse = z
  .object({
    status: z.string().catch(''),
    result: z
      .array(
        z.object({
          entity: z.string().catch(''),
          features: z.array(z.record(z.union([z.string(), z.array(z.string())]))).catch([]),
        }).passthrough(),
      )
      .catch([]),
  })
  .passthrough()

export interface OutlierRecommendation {
  entity: string
  features: { fn: string; field: string; value: string | null }[]
}

export function readOutlierRecommendations(
  res: z.infer<typeof outlierRecommendationsResponse>,
): OutlierRecommendation[] {
  return res.result.map((r) => ({
    entity: r.entity,
    features: r.features.flatMap((f) => {
      const [fn, v] = Object.entries(f)[0] ?? []
      if (!fn || v === undefined) return []
      return [{ fn, field: Array.isArray(v) ? (v[0] ?? '') : v, value: Array.isArray(v) ? (v[1] ?? '') : null }]
    }),
  }))
}

/* ------------------------------------------------------------------ api */

export interface WorkbookJobsApi {
  /** Starts the block's job; resolves to the task id its result lives under. */
  invoke: (q: { clusterId: string; body: Record<string, unknown> }) => Promise<string>
  recommendations: (q: { clusterId: string; scopeId: string; stream: string }) => Promise<OutlierRecommendation[]>
}

export function createWorkbookJobsApi(client: ApiClient): WorkbookJobsApi {
  return {
    invoke: async ({ clusterId, body }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/job/invoke`, {
        body,
        schema: blockInvokeResponse,
      })
      const id = blockTaskId(res)
      const inner = Array.isArray(res.data) ? null : res.data
      // pythonblock answers {id, status: 'PENDING'} with no envelope status.
      if ((res.status && res.status !== 'success' && res.status !== 'PENDING') || !id) {
        const msg =
          res.message ?? res.parser_error_info?.error_msg ?? (inner?.message || 'The engine did not start this block.')
        throw new SearchQueryError(msg, res.parser_error_info?.parsed_query ?? null, res)
      }
      return id
    },
    recommendations: async ({ clusterId, scopeId, stream }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/outlier/recommendations`, {
        body: { stream, scope_id: scopeId },
        schema: outlierRecommendationsResponse,
      })
      return readOutlierRecommendations(res)
    },
  }
}
